import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const NavItem = ({ to, children, hoveredItem, setHoveredItem, layoutId, onClick }) => {
  const isHovered = hoveredItem === layoutId;

  return (
    <div
      className="relative px-5 py-2.5"
      onMouseEnter={() => setHoveredItem(layoutId)}
      onMouseLeave={() => setHoveredItem(null)}
    >
      {isHovered && (
        <motion.div
          layoutId="nav-pill"
          className="absolute inset-0 bg-black/5 rounded-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
        />
      )}
      <NavLink
        to={to}
        onClick={onClick}
        className={({ isActive }) =>
          `relative z-10 text-sm font-medium transition-colors whitespace-nowrap ${isActive ? 'text-black font-semibold' : 'text-neutral-600 hover:text-black'}`
        }
      >
        {children}
      </NavLink>
    </div>
  );
};

// Spring config matching iPhone Dynamic Island feel
const islandSpring = { type: "spring", stiffness: 400, damping: 32 };
const contentFade = { duration: 0.2, ease: "easeInOut" };

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isNavHovered, setIsNavHovered] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);

  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (latest) => {
    setIsScrolled(latest > 20);
  });

  // Expanded when at top OR when hovering the compressed pill
  const showFull = !isScrolled || isNavHovered;

  return (
    <>
      {/* ── Desktop Dynamic Island ── */}
      <motion.nav
        className="fixed z-50 hidden md:block top-6 left-0 right-0 mx-auto rounded-full border border-white/40 shadow-2xl shadow-black/10 bg-white/90 backdrop-blur-2xl saturate-150"
        onMouseEnter={() => setIsNavHovered(true)}
        onMouseLeave={() => { setIsNavHovered(false); setHoveredItem(null); }}
        initial={{ y: -100, opacity: 0 }}
        animate={{
          y: 0,
          opacity: 1,
          width: showFull ? "min(95vw, 64rem)" : "280px",
        }}
        transition={islandSpring}
        style={{ maxWidth: "none", overflow: "hidden" }}
      >
        <div className={`px-6 py-3 ${showFull ? 'grid grid-cols-[1fr_auto_1fr]' : 'flex justify-center'} items-center gap-4`}>
          {/* Logo  -  always visible */}
          <NavLink to="/" className="flex items-center gap-3 group relative z-50 shrink-0 justify-self-start">
            <div className="relative w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-sm border border-black/5">
              <div className="absolute inset-0 bg-blue-500/10 rounded-full animate-pulse-slow" />
              <div className="w-3 h-3 bg-[#1d1d1f] rounded-full group-hover:scale-125 transition-transform duration-500" />
            </div>
            <span className="text-lg font-bold tracking-tight text-[#1d1d1f] whitespace-nowrap">
              Virtual Beamline
            </span>
          </NavLink>

          {/* Nav links  -  centered column */}
          {showFull && (
            <motion.div
              className="flex items-center justify-center"
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={contentFade}
              key="nav-center"
            >
              <div className="flex items-center gap-1 bg-neutral-100/50 rounded-full p-1.5 border border-black/5" onMouseLeave={() => setHoveredItem(null)}>
                <NavItem to="/" hoveredItem={hoveredItem} setHoveredItem={setHoveredItem} layoutId="home">Home</NavItem>
                <div
                  className="relative px-5 py-2.5"
                  onMouseEnter={() => setHoveredItem('simulator')}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  {hoveredItem === 'simulator' && (
                    <motion.div
                      layoutId="nav-pill"
                      className="absolute inset-0 bg-black/5 rounded-full"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                  <a
                    href="https://simulator.virtualbeamline.org"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative z-10 text-sm font-medium transition-colors whitespace-nowrap text-neutral-600 hover:text-black"
                  >
                    Simulator
                  </a>
                </div>
                <NavItem to="/learn" onClick={() => window.scrollTo(0, 0)} hoveredItem={hoveredItem} setHoveredItem={setHoveredItem} layoutId="learn">Learn</NavItem>
                <NavItem to="/physics-circles" onClick={() => window.scrollTo(0, 0)} hoveredItem={hoveredItem} setHoveredItem={setHoveredItem} layoutId="physics-circles">Physics Circles</NavItem>
                <NavItem to="/team" onClick={() => window.scrollTo(0, 0)} hoveredItem={hoveredItem} setHoveredItem={setHoveredItem} layoutId="team">Team & Motivation</NavItem>
              </div>
            </motion.div>
          )}

          {/* CTA  -  right column */}
          {showFull && (
            <motion.div
              className="flex items-center justify-self-end"
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={contentFade}
              key="nav-cta"
            >
              <a
                href="https://simulator.virtualbeamline.org"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-3.5 bg-[#1d1d1f] hover:bg-black text-white text-base font-semibold rounded-full transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-black/10 hover:shadow-xl hover:shadow-black/20 whitespace-nowrap shrink-0"
              >
                Run Simulator
              </a>
            </motion.div>
          )}
        </div>
      </motion.nav>

      {/* ── Mobile Top Bar (unchanged) ── */}
      <motion.nav
        className="fixed z-50 md:hidden top-0 left-0 right-0 h-20 bg-white/80 backdrop-blur-xl border-b border-white/20"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, type: "spring", bounce: 0.2 }}
      >
        <div className="h-full px-6 grid grid-cols-[auto_1fr] items-center">
          <NavLink to="/" className="flex items-center gap-3 group relative z-50 shrink-0 justify-self-start">
            <div className="relative w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-sm border border-black/5">
              <div className="absolute inset-0 bg-blue-500/10 rounded-full animate-pulse-slow" />
              <div className="w-3 h-3 bg-[#1d1d1f] rounded-full group-hover:scale-125 transition-transform duration-500" />
            </div>
            <span className="text-lg font-bold tracking-tight text-[#1d1d1f]">
              VB
            </span>
          </NavLink>

          <button
            className="p-2 text-slate-800 z-50 relative justify-self-end"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-white/90 backdrop-blur-3xl pt-28 px-8 md:hidden flex flex-col"
          >
            <div className="space-y-6">
              {[
                { to: "/", label: "Home" },
                { to: null, label: "Simulator", href: "https://simulator.virtualbeamline.org" },
                { to: "/learn", label: "Learn" },
                { to: "/team", label: "Team & Motivation" },
              ].map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 + 0.1, duration: 0.5, type: "spring", bounce: 0.3 }}
                >
                  {item.href ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block text-4xl font-semibold tracking-tighter transition-colors text-neutral-400 hover:text-black"
                    >
                      {item.label}
                    </a>
                  ) : (
                  <NavLink
                    to={item.to}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={({ isActive }) => `block text-4xl font-semibold tracking-tighter transition-colors ${isActive ? 'text-black' : 'text-neutral-400'}`}
                  >
                    {item.label}
                  </NavLink>
                  )}
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-auto mb-12"
            >
              <a
                href="https://simulator.virtualbeamline.org"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full py-5 bg-black text-white text-xl text-center font-bold rounded-3xl"
              >
                Run Simulator
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
